"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultationForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="consultation-form" ref={ref} className={`py-24 px-4 relative overflow-hidden transition-opacity duration-150 ease-out ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-repeat" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='%23000' fill-opacity='1'/%3E%3C/svg%3E")`,
      backgroundSize: "60px 60px"
    }} />

      <div className="max-w-2xl mx-auto relative">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight mb-4">
            Request a Consultation
          </h2>
          <p className="text-muted-foreground text-lg">
            Fill out the form below and our team will reach out to discuss how
            we can support your practice.
          </p>
        </div>

        <FormProvider className="bg-white rounded-lg border border-border shadow-soft p-8 md:p-10" formId="consultation_form_get_started" sectionName="consultation-form" defaultValues={{
        name: "",
        organization_name: "",
        email: "",
        phone: "",
        services_requested: [],
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        organization_name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        services_requested: {
          required: true
        }
      }} submitText="Request a Consultation" successMessage="Thank you! We've received your consultation request and will contact you shortly.">
          <ConsultationFormFields />
        </FormProvider>
      </div>
    </section>;
}
function ConsultationFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const serviceOptions = ["ABA Billing Assistance", "Provider Credentialing Support", "Claims Follow-Up & Denial Support", "Practice Administrative Support", "Insurance & Payer Coordination"];
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary/20 rounded-lg text-center">
        <div className="mb-4">
          <svg className="w-16 h-16 mx-auto text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-foreground text-lg font-medium mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" size="lg">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="h-11 transition-all duration-120 focus:border-primary focus:shadow-medium" {...field} />
              </FormControl>
              <FormDescription>First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization_name" rules={{
        required: "Organization name is required",
        minLength: {
          value: 2,
          message: "Organization name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Organization name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Organization Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Organization" className="h-11 transition-all duration-120 focus:border-primary focus:shadow-medium" {...field} />
              </FormControl>
              <FormDescription>Name of the ABA or behavioral health practice</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="h-11 transition-all duration-120 focus:border-primary focus:shadow-medium" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s\-\(\)\+]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="h-11 transition-all duration-120 focus:border-primary focus:shadow-medium" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="services_requested" rules={{
        required: "Please select at least one service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Services Requested</FormLabel>
              <FormDescription>
                Select one or more services you are interested in
              </FormDescription>
              <div className="space-y-3 mt-3">
                {serviceOptions.map((service, i) => <div key={i} data-index={i} className="flex items-start space-x-3">
                    <Checkbox checked={field.value?.includes(service)} onCheckedChange={checked => {
              const updatedValue = checked ? [...(field.value || []), service] : (field.value || []).filter((val: string) => val !== service);
              field.onChange(updatedValue);
            }} className="mt-0.5 transition-transform duration-80 hover:scale-110" />
                    <label className="text-sm font-medium leading-none cursor-pointer select-none">
                      {service}
                    </label>
                  </div>)}
              </div>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel>Additional Details (Optional)</FormLabel>
              <FormControl>
                <Textarea placeholder="Briefly describe your needs or any specific questions..." className="min-h-32 transition-all duration-120 focus:border-primary focus:shadow-medium resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold shadow-medium transition-all duration-80 hover:brightness-95 hover:-translate-y-0.5 active:translate-y-0">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Submitting...
            </> : "Request a Consultation"}
        </Button>
      </div>
    </Form>;
}